/* Vendored from the board kit on feat/board-kit-legend-scroll (b834d95, verifier-CONFIRMED, not yet on master).
 * Manifest Review needs the docked record and the top scroll rail now; when that branch lands, import this
 * from components/board instead and delete this copy. Kept byte-close to the kit on purpose. */
/**
 * The board table shell (generic; docs/design/boards.md).
 *
 *   - a scroll frame that is a labelled, focusable region, so the keyboard
 *     can scroll a table wider than the screen, with a synced horizontal
 *     scroll bar on TOP as well as its own at the bottom (DualScroll);
 *   - a sticky header and a FROZEN identity column (`th scope="row"`), so a
 *     row's name stays while the columns move;
 *   - the identity cell holds the row's open/close button (aria-expanded,
 *     aria-controls), and a click anywhere else on the row does the same
 *     unless it landed on a control or ended a text selection;
 *   - the open row is highlighted and its record opens DOCKED beside the
 *     table (DetailDock), never as a row unfolding inside it. Escape closes
 *     and focus returns to the row; Previous / Next walk this page's rows;
 *     below 900px the dock is a full-height sheet, and a MODAL one: the rest
 *     of the page behind it is inert and hidden from assistive technology,
 *     and the page does not scroll under it.
 *   - Escape and Alt+Up / Alt+Down work wherever focus is on the board, or
 *     when it has fallen to the page body (a button that just disabled
 *     itself), never inside a field or a dialog of the record's own.
 *
 * The open record is the caller's `openKey` (the URL's `open`), so a link or
 * a refresh lands on it. It renders what it is given. Which rows exist, their
 * order and the page are the server's.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import SortHead from '../../../components/board/SortHead';
import DualScroll from './DualScroll';
import DetailDock, { useNarrow } from './DetailDock';
import type { BoardDetailSection } from './DetailDock';
import { memorySlug } from './memory';
import type { BoardColumn } from '../../../components/board/types';

export interface BoardIdentity<T> {
  label: string;
  help: string;
  sort?: string;
  render: (row: T) => React.ReactNode;
  /** The name the open button speaks ("Open NORTH RIDGE"). */
  name: (row: T) => string;
  /** False = this row has no panel (e.g. an account with no resident); `why` is said instead. */
  openable?: (row: T) => boolean;
  closedWhy?: (row: T) => string;
}

export interface BoardTableProps<T> {
  label: string;
  columns: BoardColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  identity: BoardIdentity<T>;
  openKey: string | null;
  /** Called with a row's key to open it, or with the open row's key to close it. */
  onToggle: (key: string) => void;
  /** Optional: set the open record directly (null closes). Without it the kit uses onToggle. */
  onOpenChange?: (key: string | null) => void;
  /** The open record's content as one section (Scheduling and Finance). */
  renderDetail?: (row: T) => React.ReactNode;
  /** Or as sections, with an anchor list and collapsible blocks. Takes precedence over renderDetail. */
  detailSections?: (row: T) => BoardDetailSection[];
  /** The open record's accessible name. */
  detailLabel: (row: T) => string;
  /** Where collapsed sections are remembered; defaults to the identity label. */
  memoryKey?: string;
  sort: string | null;
  dir: 'asc' | 'desc';
  sorts: string[];
  onSort: (col: string, dir: 'asc' | 'desc') => void;
  busy?: boolean;
  rowClass?: (row: T) => string;
}

const dockId = (label: string) => `board-dock-${memorySlug(label)}`;

function isEditable(t: HTMLElement): boolean {
  return Boolean(t.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]'));
}

export default function BoardTable<T>({
  label, columns, rows, rowKey, identity, openKey, onToggle, onOpenChange, renderDetail, detailSections, detailLabel, memoryKey,
  sort, dir, sorts, onSort, busy = false, rowClass,
}: BoardTableProps<T>) {
  // Header groups (optional): runs of adjacent columns with the same `group` share one labelled cell above them.
  const groups: Array<{ label: string; span: number; first: string }> = [];
  for (const c of columns) {
    const g = c.group || '';
    const last = groups[groups.length - 1];
    if (last && last.label === g) last.span += 1; else groups.push({ label: g, span: 1, first: c.key });
  }
  const grouped = groups.some((g) => g.label);
  const groupStart = (key: string) => groups.some((g) => g.label && g.first === key);

  const isOpenable = (r: T) => (identity.openable ? identity.openable(r) : true);
  const openableKeys = rows.filter(isOpenable).map(rowKey);
  const openRow = openKey ? rows.find((r) => rowKey(r) === openKey && isOpenable(r)) || null : null;
  const openIndex = openRow ? openableKeys.indexOf(openKey as string) : -1;
  const sheet = useNarrow(899);
  const dock = dockId(identity.label);

  const frameRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const openBtns = useRef<Record<string, HTMLButtonElement | null>>({});
  const rowEls = useRef<Record<string, HTMLTableRowElement | null>>({});
  const focusPanel = useRef(false);
  const returnTo = useRef<string | null>(null);

  const setOpen = useCallback((key: string | null) => {
    if (onOpenChange) { onOpenChange(key); return; }
    if (key === null) { if (openKey) onToggle(openKey); return; }
    if (key !== openKey) onToggle(key);
  }, [onOpenChange, onToggle, openKey]);

  const toggleRow = (key: string) => {
    if (key === openKey) { setOpen(null); return; }
    focusPanel.current = true;
    setOpen(key);
  };

  const close = useCallback(() => {
    if (!openKey) return;
    returnTo.current = openKey;
    setOpen(null);
  }, [openKey, setOpen]);

  const step = useCallback((delta: number) => {
    if (openIndex < 0) return;
    const at = openIndex + delta;
    const next = openableKeys[at];
    if (!next) return;
    // Reaching the first or last record disables the button just pressed; focus would fall to the page.
    // Hand it to the record's heading instead, so the keyboard stays in the panel (and Escape keeps working).
    const edge = at === 0 || at === openableKeys.length - 1;
    const active = typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
    if (edge && active && active.closest && active.closest('.board-dock__nav-btns')) focusPanel.current = true;
    setOpen(next);
    const el = rowEls.current[next];
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [openIndex, openableKeys, setOpen]);

  // As a sheet the record covers the page, as a modal: everything behind it (the table, the board's head,
  // legend, pager and foot, the shell around them) is inert and hidden from assistive technology, and the
  // page does not scroll under it. A layout effect, so it is all live again before focus returns to the row.
  const covered = Boolean(openRow && sheet);
  useLayoutEffect(() => {
    if (!covered) return undefined;
    const body = bodyRef.current;
    const dockEl = body ? body.querySelector<HTMLElement>('.board-dock') : null;
    if (!dockEl || typeof document === 'undefined') return undefined;
    const made: HTMLElement[] = [];
    let node: HTMLElement = dockEl;
    // Every sibling along the way up to the app root (#root's own siblings, such as toasts, stay live).
    while (node.parentElement && node.parentElement !== document.body && node.parentElement !== document.documentElement) {
      for (const sib of Array.from(node.parentElement.children)) {
        if (sib === node || !(sib instanceof HTMLElement) || /^(SCRIPT|STYLE|TEMPLATE)$/.test(sib.tagName)) continue;
        if (sib.hasAttribute('inert')) continue;
        sib.setAttribute('inert', '');
        sib.setAttribute('aria-hidden', 'true');
        made.push(sib);
      }
      node = node.parentElement;
    }
    const html = document.documentElement;
    const prevOverflow = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => {
      for (const el of made) { el.removeAttribute('inert'); el.removeAttribute('aria-hidden'); }
      html.style.overflow = prevOverflow;
    };
  }, [covered]);

  // A link or a refresh that names an open record shows its row, without moving focus.
  useEffect(() => {
    if (!openKey) return;
    const el = rowEls.current[openKey];
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
    // Only on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus follows the person: into the panel when they open a row, back to that row when they close it.
  useEffect(() => {
    if (openRow && focusPanel.current) {
      focusPanel.current = false;
      headingRef.current?.focus();
    }
    if (!openKey && returnTo.current) {
      const k = returnTo.current;
      returnTo.current = null;
      const btn = openBtns.current[k];
      if (btn) btn.focus(); else frameRef.current?.focus();
    }
  }, [openKey, openRow]);

  // The open record's keys, on the document while a record is open, so they do not depend on focus
  // being inside the board (a disabled button drops focus to the page body).
  const keys = useRef<{ close: () => void; step: (d: number) => void }>({ close, step });
  keys.current = { close, step };
  const isOpen = Boolean(openRow);
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const t = e.target instanceof HTMLElement ? e.target : null;
      // Only from the board itself or from nowhere in particular (the page body); a key meant for another part of the page is left alone.
      const own = !t || t === document.body || t === document.documentElement || Boolean(bodyRef.current && bodyRef.current.contains(t));
      if (!own) return;
      // A dialog inside the record owns its own Escape; a field keeps its keys.
      if (t && (t.closest('[role="dialog"], dialog') || isEditable(t))) return;
      if (e.key === 'Escape') { e.preventDefault(); keys.current.close(); }
      else if (e.altKey && e.key === 'ArrowDown') { e.preventDefault(); keys.current.step(1); }
      else if (e.altKey && e.key === 'ArrowUp') { e.preventDefault(); keys.current.step(-1); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const sections: BoardDetailSection[] = openRow
    ? (detailSections ? detailSections(openRow) : [{ id: 'detail', label: detailLabel(openRow), render: () => (renderDetail ? renderDetail(openRow) : null) }])
    : [];

  return (
    <div className={`board-body${openRow ? ' has-dock' : ''}${covered ? ' has-sheet' : ''}`} ref={bodyRef}>
      <div className="board-tablecol">
        <DualScroll label={label} frameClassName="board-scroll" frameRef={frameRef}>
          <table className={`board-table${grouped ? ' board-table--grouped' : ''}`} aria-busy={busy || undefined}>
            <thead>
              {grouped && (
                <tr className="board-grouprow">
                  <td className="board-col-id" />
                  {groups.map((g) => (g.label
                    ? <th key={g.first} scope="colgroup" colSpan={g.span} className="board-grouphead" data-group={g.label}>{g.label}</th>
                    : <td key={g.first} colSpan={g.span} />))}
                </tr>
              )}
              <tr>
                <SortHead label={identity.label} col={identity.sort} sort={sort} dir={dir} sorts={sorts} onSort={onSort} help={identity.help} className="board-col-id" />
                {columns.map((c) => (
                  <SortHead key={c.key} label={c.label} col={c.sort} sort={sort} dir={dir} sorts={sorts} onSort={onSort} help={c.help} className={[c.numeric ? 'board-num' : '', c.group ? `board-in-group${groupStart(c.key) ? ' board-group-start' : ''}` : ''].filter(Boolean).join(' ') || undefined} />
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const key = rowKey(r);
                const openable = isOpenable(r);
                const open = openable && openKey === key;
                return (
                  <tr
                    key={key}
                    ref={(el) => { rowEls.current[key] = el; }}
                    data-key={key}
                    aria-current={open ? 'true' : undefined}
                    className={`board-row${open ? ' is-open' : ''}${openable ? ' is-openable' : ''}${rowClass ? ` ${rowClass(r)}` : ''}`}
                    onClick={(e) => {
                      if (!openable) return;
                      if ((e.target as HTMLElement).closest('button, a, input, select, textarea, summary')) return;
                      const sel = typeof window !== 'undefined' && window.getSelection ? window.getSelection() : null;
                      if (sel && !sel.isCollapsed && sel.toString().trim()) return;
                      toggleRow(key);
                    }}
                  >
                    <th scope="row" className="board-col-id">
                      {openable ? (
                        <button
                          type="button"
                          ref={(el) => { openBtns.current[key] = el; }}
                          className="board-open"
                          aria-expanded={open}
                          aria-controls={open ? dock : undefined}
                          aria-label={`${open ? 'Close' : 'Open'} ${identity.name(r)}`}
                          onClick={(e) => { e.stopPropagation(); toggleRow(key); }}
                        >
                          <span className="board-caret" aria-hidden="true" />
                          <span className="board-open__name">{identity.render(r)}</span>
                        </button>
                      ) : (
                        <span className="board-closed">
                          <span className="board-open__name">{identity.render(r)}</span>
                          {identity.closedWhy && <span className="board-closed__why">{identity.closedWhy(r)}</span>}
                        </span>
                      )}
                    </th>
                    {columns.map((c) => <td key={c.key} className={[c.numeric ? 'board-num' : '', c.group ? `board-in-group${groupStart(c.key) ? ' board-group-start' : ''}` : ''].filter(Boolean).join(' ') || undefined}>{c.render(r)}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </DualScroll>
      </div>
      {openRow && (
        <DetailDock
          id={dock}
          label={detailLabel(openRow)}
          title={identity.render(openRow)}
          index={openIndex + 1}
          count={openableKeys.length}
          onPrev={openIndex > 0 ? () => step(-1) : null}
          onNext={openIndex >= 0 && openIndex < openableKeys.length - 1 ? () => step(1) : null}
          onClose={close}
          sheet={sheet}
          sections={sections}
          memoryKey={memoryKey || memorySlug(identity.label)}
          headingRef={headingRef}
        />
      )}
    </div>
  );
}
